import { apiService } from './api';

export interface Stock {
  id: number;
  symbol: string;
  name: string;
  exchange: string;
}

export interface StockPrice {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface TechnicalAnalysis {
  symbol: string;
  analyzed_at: string;
  price_statistics: {
    current_price: number;
    lifetime_high: number;
    lifetime_low: number;
    one_year_return: number;
    month_return: number;
    week_return: number;
  };
  crossover_analysis: {
    last_golden_crossover?: {
      date: string;
      price: number;
      change_since: number;
    };
    last_death_crossover?: {
      date: string;
      price: number;
      change_since: number;
    };
  };
  current_indicators: {
    rsi: number;
    sma50: number;
    sma200: number;
    ema50: number;
    ema200: number;
    support: number;
    resistance: number;
  };
}

export interface ScrapedData {
  stock_symbol: string;
  scraped_at: string;
  significant_values: Record<string, number>;
  peer_comparison: string[][];
  quarterly_results: string[][];
  profit_loss: string[][];
}

export const stocksService = {
  // Existing methods
  getAllStocks: async (): Promise<Stock[]> => {
    const response: any = await apiService.get('/stocks');
    return response.stocks;
  },

  searchStocks: async (query: string): Promise<Stock[]> => {
    const response: any = await apiService.get(`/stocks/search?q=${encodeURIComponent(query)}`);
    return response.stocks;
  },

  // New methods for enhanced functionality
  getStockList: async (): Promise<{ symbols: string[]; count: number }> => {
    const response: any = await apiService.get('/stocks/stock-list');
    return response;
  },

  updateStockPrices: async (): Promise<{ message: string; status: string }> => {
    const response: any = await apiService.post('/stocks/update-prices');
    return response;
  },

  getStockPrices: async (
    symbol: string,
    startDate?: string,
    endDate?: string
  ): Promise<{ symbol: string; prices: StockPrice[]; count: number }> => {
    let url = `/stocks/prices/${symbol}`;
    const params = new URLSearchParams();
    
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response: any = await apiService.get(url);
    return response;
  },

  getLatestPrice: async (symbol: string): Promise<{ symbol: string; price: StockPrice }> => {
    const response: any = await apiService.get(`/stocks/latest-price/${symbol}`);
    return response;
  },

  // Technical Analysis
  startTechnicalAnalysis: async (): Promise<{ message: string; status: string }> => {
    const response: any = await apiService.post('/stocks/technical-analysis');
    return response;
  },

  getTechnicalAnalysis: async (symbol: string): Promise<{ symbol: string; analysis: TechnicalAnalysis }> => {
    const response: any = await apiService.get(`/stocks/technical-analysis/${symbol}`);
    return response;
  },

  getTechnicalSummary: async (): Promise<{ data: any[]; count: number }> => {
    const response: any = await apiService.get('/stocks/technical-summary');
    return response;
  },

  // Web Scraping
  startScraping: async (): Promise<{ message: string; status: string }> => {
    const response: any = await apiService.post('/stocks/scrape');
    return response;
  },

  getScrapedData: async (symbol: string): Promise<{ symbol: string; data: ScrapedData }> => {
    const response: any = await apiService.get(`/stocks/scraped-data/${symbol}`);
    return response;
  },

  getScrapedSummary: async (): Promise<{ data: any[]; count: number }> => {
    const response: any = await apiService.get('/stocks/scraped-summary');
    return response;
  },

  // Notifications
  sendTriggerNotification: async (data: {
    stockname: string;
    trigger_type?: string;
    target_price?: number;
    current_price?: number;
    message?: string;
  }): Promise<{ message: string; status: string }> => {
    const response: any = await apiService.post('/stocks/trigger-notification', data);
    return response;
  },
}; 